import type { EvidenceItem, Matter, Obligation, RedlineRound, RiskItem } from "./mockState";
import type { LiveDocumentRecord } from "../hooks/useLiveSurfaceRecords";

export type ToneLabel = "urgent" | "concerned" | "frustrated" | "collaborative" | "neutral";

export interface ToneAnalysisInput {
  id: string;
  clientName: string;
  sender: string;
  subject: string;
  body: string;
  role?: "CLIENT" | "LAWYER" | "AI_DISPATCH";
  sourcePath?: string;
}

export interface ToneAnalysisResult {
  messageId: string;
  clientName: string;
  label: ToneLabel;
  confidence: number;
  cues: string[];
  responsePosture: string;
  draftingGuidance: string;
  sourcePath?: string;
}

const toneSignals: Array<{ label: ToneLabel; weight: number; terms: string[] }> = [
  { label: "urgent", weight: 26, terms: ["urgent", "asap", "immediately", "deadline", "before signing", "next wednesday", "action required", "overdue"] },
  { label: "frustrated", weight: 28, terms: ["unacceptable", "delay", "delayed", "failed", "breach", "complaint", "escalate", "not happy", "manual feedback"] },
  { label: "concerned", weight: 22, terms: ["concern", "double check", "risk", "warning", "flag", "can we", "please confirm", "alignment"] },
  { label: "collaborative", weight: 18, terms: ["thanks", "reviewed", "looks clean", "agree", "approve", "happy to", "together"] },
];

const toneCopy: Record<ToneLabel, { responsePosture: string; draftingGuidance: string }> = {
  urgent: {
    responsePosture: "Prompt, specific, and deadline-led",
    draftingGuidance: "Acknowledge the time pressure first, confirm the concrete next step, and avoid open-ended legal analysis in the first sentence.",
  },
  concerned: {
    responsePosture: "Reassuring and evidence-led",
    draftingGuidance: "Name the issue, cite the record or review step you will check, and separate confirmed facts from counsel judgment.",
  },
  frustrated: {
    responsePosture: "Calm, accountable, and de-escalating",
    draftingGuidance: "Avoid defensive wording. Lead with ownership, provide a short corrective path, and keep privilege-sensitive analysis out of the client-facing reply.",
  },
  collaborative: {
    responsePosture: "Concise and partnership-oriented",
    draftingGuidance: "Confirm alignment, capture the decision needed, and preserve momentum with one clear action owner.",
  },
  neutral: {
    responsePosture: "Matter-of-fact and scoped",
    draftingGuidance: "Keep the response direct, confirm the requested action, and do not infer emotion beyond the message text.",
  },
};

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
}

export function analyzeCommunicationTone(input: ToneAnalysisInput): ToneAnalysisResult {
  const text = normalizeText(`${input.subject} ${input.body}`);
  const scores = new Map<ToneLabel, number>();
  const cues: string[] = [];

  toneSignals.forEach((signal) => {
    signal.terms.forEach((term) => {
      if (text.includes(term)) {
        scores.set(signal.label, (scores.get(signal.label) ?? 0) + signal.weight);
        cues.push(term);
      }
    });
  });

  if (/[!?]{2,}/.test(input.body) || /\b[A-Z]{4,}\b/.test(input.body)) {
    scores.set("urgent", (scores.get("urgent") ?? 0) + 12);
    cues.push("emphasis");
  }

  const ranked = [...scores.entries()].sort((left, right) => right[1] - left[1]);
  const label = ranked[0]?.[0] ?? "neutral";
  const rawScore = ranked[0]?.[1] ?? 12;
  const uniqueCues = [...new Set(cues)].slice(0, 4);
  const confidence = Math.min(96, Math.max(58, rawScore + uniqueCues.length * 6));
  const copy = toneCopy[label];

  return {
    messageId: input.id,
    clientName: input.clientName,
    label,
    confidence,
    cues: uniqueCues.length > 0 ? uniqueCues : ["plain-language message"],
    responsePosture: copy.responsePosture,
    draftingGuidance: copy.draftingGuidance,
    sourcePath: input.sourcePath,
  };
}

export interface ContractDeviationFinding {
  id: string;
  roundId: number;
  section: string;
  status: RedlineRound["clauseChanges"][number]["status"];
  severity: RiskItem["severity"];
  originalText: string;
  proposedText: string;
  aiConfidence: string;
  linkedRisk?: RiskItem;
}

export interface ContractReviewState {
  matterId: string;
  sourceLabel: string;
  playbookRating: number;
  openDeviationCount: number;
  unresolvedRoundCount: number;
  obligationCount: number;
  overdueObligationCount: number;
  deviations: ContractDeviationFinding[];
  risks: RiskItem[];
  obligations: Obligation[];
  reportDigest: string;
}

function severityRank(severity: RiskItem["severity"]): number {
  return { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 }[severity];
}

function findLinkedRisk(change: RedlineRound["clauseChanges"][number], risks: RiskItem[]): RiskItem | undefined {
  const section = normalizeText(change.section.replace(/[§:]/g, " "));
  return risks
    .map((risk) => ({ risk, score: normalizeText(`${risk.clause} ${risk.description}`).split(" ").filter((word) => word.length > 4 && section.includes(word)).length }))
    .sort((left, right) => right.score - left.score)[0]?.risk;
}

export function buildContractReviewState(matter: Matter): ContractReviewState {
  const deviations = matter.redlineRounds.flatMap((round) => round.clauseChanges.map((change) => {
    const linkedRisk = findLinkedRisk(change, matter.riskRegister);
    const severity = linkedRisk?.severity ?? (change.status === "PENDING" ? "HIGH" : "LOW");
    return {
      id: `${matter.matterId}-${round.roundId}-${change.section}`,
      roundId: round.roundId,
      section: change.section,
      status: change.status,
      severity,
      originalText: change.originalText,
      proposedText: change.proposedText,
      aiConfidence: change.aiConfidence,
      linkedRisk,
    } satisfies ContractDeviationFinding;
  })).sort((left, right) => severityRank(right.severity) - severityRank(left.severity));

  const openDeviationCount = deviations.filter((deviation) => deviation.status === "PENDING").length;
  const unresolvedRoundCount = matter.redlineRounds.filter((round) => round.status !== "RESOLVED").length;
  const overdueObligationCount = matter.obligations.filter((obligation) => obligation.status === "OVERDUE").length;
  const baseRating = 100 - openDeviationCount * 14 - unresolvedRoundCount * 6 - overdueObligationCount * 8 - matter.riskRegister.filter((risk) => risk.severity === "CRITICAL").length * 10;
  const playbookRating = Math.max(24, Math.min(100, baseRating));
  const leadDeviation = deviations.find((deviation) => deviation.status === "PENDING") ?? deviations[0];

  return {
    matterId: matter.matterId,
    sourceLabel: `${matter.matterId} / ${matter.lifecycle}`,
    playbookRating,
    openDeviationCount,
    unresolvedRoundCount,
    obligationCount: matter.obligations.length,
    overdueObligationCount,
    deviations,
    risks: matter.riskRegister,
    obligations: matter.obligations,
    reportDigest: leadDeviation
      ? `${matter.title}: ${openDeviationCount} open deviation(s), ${matter.riskRegister.length} risk finding(s), lead issue ${leadDeviation.section}.`
      : `${matter.title}: no clause deviations recorded against the current matter review state.`,
  };
}

export type BundleReadiness = "READY" | "NEEDS_REVIEW" | "BLOCKED";

export interface CaseBundleDocument {
  id: string;
  name: string;
  groupKey: "pleadings" | "exhibits" | "internal" | "repository";
  groupLabel: string;
  size: string;
  status: string;
  sourcePath: string;
  checksum: string;
}

export interface CaseBundleGroup {
  key: CaseBundleDocument["groupKey"];
  label: string;
  documents: CaseBundleDocument[];
}

export interface CaseBundlePlan {
  matterId: string;
  readiness: BundleReadiness;
  bundleId: string;
  sealedHash: string;
  totalDocuments: number;
  validatedDocuments: number;
  blockers: string[];
  groups: CaseBundleGroup[];
}

function simpleHash(input: string): string {
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = Math.imul(31, hash) + input.charCodeAt(index) | 0;
  }
  return Math.abs(hash).toString(16).toUpperCase().padStart(8, "0").slice(0, 12);
}

function evidenceGroup(evidence: EvidenceItem): CaseBundleDocument["groupKey"] {
  if (["Court Order", "Expert Report", "Witness Statement"].includes(evidence.type)) return "pleadings";
  if (evidence.status === "PRIVILEGED" || evidence.status === "REDACTED_READY") return "internal";
  return "exhibits";
}

const groupLabels: Record<CaseBundleDocument["groupKey"], string> = {
  pleadings: "Primary pleadings and court filings",
  exhibits: "Disclosure exhibits and evidence",
  internal: "Privileged internal preparation",
  repository: "Firm repository linked documents",
};

export function buildCaseBundlePlan(matter: Matter, firmDocuments: LiveDocumentRecord[] = []): CaseBundlePlan {
  const evidenceDocuments: CaseBundleDocument[] = matter.evidence.map((evidence) => {
    const groupKey = evidenceGroup(evidence);
    return {
      id: evidence.id,
      name: evidence.name,
      groupKey,
      groupLabel: groupLabels[groupKey],
      size: evidence.size,
      status: evidence.status,
      sourcePath: `matter:${matter.matterId}/evidence/${evidence.id}`,
      checksum: evidence.status === "VALIDATED" ? simpleHash(`${matter.matterId}:${evidence.id}:${evidence.name}`) : "pending-validation",
    };
  });

  const repositoryDocuments: CaseBundleDocument[] = firmDocuments
    .filter((document) => !document.deleted)
    .filter((document) => {
      const haystack = normalizeText(`${document.name} ${document.category} ${document.sourcePath}`);
      return haystack.includes(normalizeText(matter.matterId)) || haystack.includes(normalizeText(matter.clientName)) || document.category === "Evidence Logs";
    })
    .map((document) => ({
      id: document.id,
      name: document.name,
      groupKey: "repository" as const,
      groupLabel: groupLabels.repository,
      size: document.size,
      status: document.legalHold ? "LEGAL_HOLD" : "REPOSITORY_LINKED",
      sourcePath: document.sourcePath,
      checksum: document.checksum,
    }));

  const documents = [...evidenceDocuments, ...repositoryDocuments];
  const blockers: string[] = [];
  const validatedDocuments = documents.filter((document) => ["VALIDATED", "REDACTED_READY", "LEGAL_HOLD", "REPOSITORY_LINKED"].includes(document.status)).length;

  if (documents.length === 0) blockers.push("No matter evidence or linked repository documents are available for consolidation.");
  if (evidenceDocuments.some((document) => ["NEW", "ANALYZING", "INTEGRITY_COMPROMISED"].includes(document.status))) {
    blockers.push("One or more evidence records still need validation before bundle sealing.");
  }
  if (repositoryDocuments.length === 0) blockers.push("No firm repository records are linked to this case bundle yet.");

  const readiness: BundleReadiness = blockers.some((blocker) => blocker.startsWith("No matter"))
    ? "BLOCKED"
    : blockers.length > 0
      ? "NEEDS_REVIEW"
      : "READY";

  const groups = (["pleadings", "exhibits", "internal", "repository"] as CaseBundleDocument["groupKey"][])
    .map((key) => ({ key, label: groupLabels[key], documents: documents.filter((document) => document.groupKey === key) }))
    .filter((group) => group.documents.length > 0);

  const hashInput = documents.map((document) => `${document.id}:${document.checksum}:${document.status}`).join("|");

  return {
    matterId: matter.matterId,
    readiness,
    bundleId: `BUNDLE-${matter.matterId}-${documents.length}`,
    sealedHash: simpleHash(hashInput || matter.matterId),
    totalDocuments: documents.length,
    validatedDocuments,
    blockers,
    groups,
  };
}
